import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Sparkles,
  Plus,
  History,
  LogOut,
  Settings,
  Crown,
  Menu,
  X,
  Loader2,
  Copy,
  Check,
  Globe,
  RefreshCw,
  Trash2
} from 'lucide-react';
import { getTranslation } from '../translations';
import './Dashboard.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Dashboard({ user, onLogout, theme, onThemeChange, language, onLanguageChange }) {
  const t = (key) => getTranslation(key, language);
  const [promptTypes, setPromptTypes] = useState([]);
  const [selectedType, setSelectedType] = useState('standard');
  const [originalPrompt, setOriginalPrompt] = useState('');
  const [improvedPrompt, setImprovedPrompt] = useState('');
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [siteSettings, setSiteSettings] = useState(null);
  const [deletingPromptId, setDeletingPromptId] = useState(null);
  const [showAllHistory, setShowAllHistory] = useState(false);
  const [trialInfo, setTrialInfo] = useState({ used: 0, remaining: 5, limit: 5 });

  useEffect(() => {
    loadPromptTypes();
    loadHistory();
    loadSiteSettings();
    loadTrialInfo();
  }, []);

  useEffect(() => {
    // Reload prompt types when returning to the page
    const handleFocus = () => {
      loadPromptTypes();
      loadSiteSettings();
      loadTrialInfo();
    };
    
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  const loadPromptTypes = async () => {
    try {
      const response = await axios.get(`${API}/prompt-types`);
      setPromptTypes(response.data);
    } catch (error) {
      console.error('Error loading prompt types:', error);
    }
  };

  const loadTrialInfo = async () => {
    try {
      const response = await axios.get(`${API}/users/me`);
      if (response.data.subscription_tier === 'free') {
        setTrialInfo({
          used: response.data.trial_prompts_used || 0,
          remaining: response.data.trial_prompts_remaining || 5,
          limit: response.data.trial_prompts_limit || 5
        });
      }
    } catch (error) {
      console.error('Error loading trial info:', error);
    }
  };

  const loadSiteSettings = async () => {
    try {
      const response = await axios.get(`${API}/site-settings`);
      setSiteSettings(response.data);
    } catch (error) {
      console.error('Error loading site settings:', error);
    }
  };

  const handleRefreshPromptTypes = async () => {
    setRefreshing(true);
    await loadPromptTypes();
    await loadSiteSettings();
    setTimeout(() => setRefreshing(false), 500);
  };

  const loadHistory = async () => {
    try {
      const response = await axios.get(`${API}/prompts/history`);
      setHistory(response.data);
    } catch (error) {
      console.error('Error loading history:', error);
    }
  };

  const handleImprovePrompt = async () => {
    if (!originalPrompt.trim()) return;

    setLoading(true);
    setError('');
    setImprovedPrompt('');

    try {
      const response = await axios.post(`${API}/prompts/improve`, {
        original_prompt: originalPrompt,
        prompt_type: selectedType
      });

      setImprovedPrompt(response.data.improved_prompt);
      
      // Update trial info if returned
      if (response.data.trial_prompts_remaining !== null && response.data.trial_prompts_remaining !== undefined) {
        setTrialInfo(prev => ({
          ...prev,
          used: prev.limit - response.data.trial_prompts_remaining,
          remaining: response.data.trial_prompts_remaining
        }));
      }
      
      loadHistory();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to improve prompt');
      if (err.response?.status === 403) {
        setShowUpgrade(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleNewPrompt = () => {
    setOriginalPrompt('');
    setImprovedPrompt('');
    setError('');
    setSelectedType('standard');
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(improvedPrompt);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const loadHistoryPrompt = async (promptId) => {
    try {
      const response = await axios.get(`${API}/prompts/${promptId}`);
      setOriginalPrompt(response.data.original_prompt);
      setImprovedPrompt(response.data.improved_prompt);
      setSelectedType(response.data.prompt_type);
      setError('');
    } catch (error) {
      console.error('Error loading prompt:', error);
    }
  };

  const handleUpgrade = async (plan) => {
    setProcessingPayment(true);
    setError('');
    try {
      const response = await axios.post(`${API}/payments/create-checkout-session`, { plan });
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to start checkout. Please try again.');
      setProcessingPayment(false);
    }
  };

  const handleDeletePrompt = async (promptId, event) => {
    event.stopPropagation(); // Prevent loading the prompt when clicking delete
    
    if (!window.confirm('Are you sure you want to delete this prompt from history?')) {
      return;
    }

    setDeletingPromptId(promptId);
    try {
      await axios.delete(`${API}/prompts/${promptId}`);
      // Reload history after deletion
      loadHistory();
      
      // If the deleted prompt was currently displayed, clear it
      if (improvedPrompt) {
        const currentPromptResponse = await axios.get(`${API}/prompts/${promptId}`).catch(() => null);
        if (!currentPromptResponse) {
          setImprovedPrompt('');
          setOriginalPrompt('');
        }
      }
    } catch (error) {
      console.error('Error deleting prompt:', error);
      setError('Failed to delete prompt');
    } finally {
      setDeletingPromptId(null);
    }
  };

  const selectedTypeInfo = promptTypes.find((t) => t.type_key === selectedType);

  return (
    <div className="dashboard" data-testid="dashboard">
      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <Sparkles size={24} />
            <h2>{siteSettings?.site_title || t('appName')}</h2>
          </div>
        </div>

        <button
          className="new-prompt-btn"
          onClick={handleNewPrompt}
          data-testid="new-prompt-btn"
        >
          <Plus size={18} />
          {t('newPrompt')}
        </button>

        <div className="sidebar-section">
          <div className="section-header">
            <History size={16} />
            <span>{t('history')}</span>
          </div>
          <div className="history-list">
            {history.length === 0 ? (
              <p className="empty-text">{t('noPromptsYet')}</p>
            ) : (
              <>
                {(showAllHistory ? history : history.slice(0, 4)).map((item) => {
                  // Get first 4 words from original prompt
                  const words = item.original_prompt.trim().split(/\s+/);
                  const preview = words.slice(0, 4).join(' ');
                  
                  return (
                    <button
                      key={item.id}
                      className="history-item-simple"
                      onClick={() => loadHistoryPrompt(item.id)}
                      data-testid={`history-item-${item.id}`}
                      title={item.original_prompt}
                    >
                      {preview}...
                    </button>
                  );
                })}
                {history.length > 4 && (
                  <button
                    className="see-more-btn"
                    onClick={() => setShowAllHistory(!showAllHistory)}
                  >
                    {showAllHistory ? 'See less...' : 'See more...'}
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        <div className="sidebar-footer">
          <div className="user-info">
            <div className="user-avatar">{user.name[0].toUpperCase()}</div>
            <div className="user-details">
              <div className="user-name">{user.name}</div>
              <div className="user-plan">
                {user.subscription_tier === 'premium' && <Crown size={12} />}
                {t(user.subscription_tier)}
              </div>
            </div>
          </div>
          {user.role === 'admin' && (
            <a href="/admin" className="sidebar-link">
              <Settings size={18} />
              {t('adminPanel')}
            </a>
          )}
          <button className="sidebar-link" onClick={onLogout} data-testid="logout-btn">
            <LogOut size={18} />
            {t('logout')}
          </button>
        </div>
      </aside>

      {/* Mobile Menu Toggle */}
      <button
        className="mobile-menu-toggle"
        onClick={() => setSidebarOpen(!sidebarOpen)}
        data-testid="mobile-menu-toggle"
      >
        {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Main Content */}
      <main className="main-content">
        <div className="content-wrapper">
          {/* Header */}
          <header className="dashboard-header">
            <div className="header-text">
              <h1>{siteSettings?.landing_title || t('landingTitle')}</h1>
              <p>{siteSettings?.landing_subtitle || t('landingSubtitle')}</p>
            </div>
            <div className="header-controls">
              <div className="language-selector">
                <Globe size={16} />
                <select 
                  value={language} 
                  onChange={(e) => onLanguageChange(e.target.value)}
                  className="language-select"
                  data-testid="language-select"
                >
                  <option value="en">English</option>
                  <option value="es">Español</option>
                </select>
              </div>
              <div className="theme-selector">
                <button
                  className={`theme-btn ${theme === 'dark' ? 'active' : ''}`}
                  onClick={() => onThemeChange('dark')}
                  data-testid="theme-dark"
                >
                  {t('dark')}
                </button>
                <button
                  className={`theme-btn ${theme === 'white' ? 'active' : ''}`}
                  onClick={() => onThemeChange('white')}
                  data-testid="theme-white"
                >
                  {t('white')}
                </button>
                <button
                  className={`theme-btn ${theme === 'terminal' ? 'active' : ''}`}
                  onClick={() => onThemeChange('terminal')}
                  data-testid="theme-terminal"
                >
                  {t('terminal')}
                </button>
              </div>
            </div>
          </header>

          {/* Upgrade Modal */}
          {showUpgrade && (
            <div className="upgrade-modal" data-testid="upgrade-modal">
              <div className="upgrade-content">
                <button
                  className="modal-close"
                  onClick={() => setShowUpgrade(false)}
                >
                  <X size={20} />
                </button>
                <h2>{t('upgradeYourPlan')}</h2>
                <p>{t('dailyLimitReached')}</p>
                <div className="pricing-cards">
                  <div className="pricing-card">
                    <h3>{t('standardPlan')}</h3>
                    <div className="price">$9{t('monthlyPrice')}</div>
                    <ul>
                      <li>100 {t('promptsPerDay')}</li>
                      <li>{t('allPromptTypes')}</li>
                      <li>{t('prioritySupport')}</li>
                    </ul>
                    <button
                      className="upgrade-btn"
                      onClick={() => handleUpgrade('standard')}
                      disabled={processingPayment}
                      data-testid="upgrade-standard-btn"
                    >
                      {processingPayment ? t('processing') : `${t('upgradeTo')} ${t('standardPlan')}`}
                    </button>
                  </div>
                  <div className="pricing-card featured">
                    <div className="featured-badge">Most Popular</div>
                    <h3>{t('premiumPlan')}</h3>
                    <div className="price">$29{t('monthlyPrice')}</div>
                    <ul>
                      <li>{t('unlimitedPrompts')}</li>
                      <li>{t('allPromptTypes')}</li>
                      <li>{t('priorityProcessing')}</li>
                      <li>{t('apiAccess')}</li>
                    </ul>
                    <button
                      className="upgrade-btn"
                      onClick={() => handleUpgrade('premium')}
                      disabled={processingPayment}
                      data-testid="upgrade-premium-btn"
                    >
                      {processingPayment ? t('processing') : `${t('upgradeTo')} ${t('premiumPlan')}`}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Trial Mode Banner */}
          {user.subscription_tier === 'free' && trialInfo.remaining === 0 && (
            <div className="trial-banner expired" data-testid="trial-expired-banner">
              <div className="trial-banner-content">
                <Crown size={24} />
                <div className="trial-text">
                  <h3>Trial Limit Reached</h3>
                  <p>Your trial of 5 free prompts has ended. Subscribe to continue improving your prompts!</p>
                </div>
                <button 
                  className="subscribe-btn"
                  onClick={() => setShowUpgrade(true)}
                >
                  Get Subscription
                </button>
              </div>
            </div>
          )}
          
          {user.subscription_tier === 'free' && trialInfo.remaining > 0 && trialInfo.remaining <= 2 && (
            <div className="trial-banner warning" data-testid="trial-warning-banner">
              <div className="trial-banner-content">
                <span className="trial-count">{trialInfo.remaining} prompts remaining</span>
                <span className="trial-message">in your trial</span>
              </div>
            </div>
          )}

          {/* Input Section */}
          <div className="prompt-input-section">
            <div className="input-section-header">
              <h3>{t('promptPlaceholder').split('...')[0]}</h3>
              <button 
                className="refresh-btn" 
                onClick={handleRefreshPromptTypes}
                disabled={refreshing}
                title="Refresh prompt types"
                data-testid="refresh-prompt-types"
              >
                <RefreshCw size={16} className={refreshing ? 'spinning' : ''} />
              </button>
            </div>
            <textarea
              className="prompt-input"
              placeholder={t('promptPlaceholder')}
              value={originalPrompt}
              onChange={(e) => setOriginalPrompt(e.target.value)}
              data-testid="prompt-input"
            />

            <button
              className="improve-btn"
              onClick={handleImprovePrompt}
              disabled={loading || !originalPrompt.trim()}
              data-testid="improve-btn"
            >
              {loading ? (
                <>
                  <Loader2 size={18} className="spinner" />
                  {t('improving')}
                </>
              ) : (
                <>
                  <Sparkles size={18} />
                  {t('improvePrompt')}
                </>
              )}
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="error-banner" data-testid="error-banner">
              {error}
            </div>
          )}

          {/* Result Section */}
          {improvedPrompt && (
            <div className="result-section" data-testid="result-section">
              <div className="result-header">
                <h3>{t('improvedPrompt')}</h3>
                <button className="copy-btn" onClick={handleCopy} data-testid="copy-btn">
                  {copied ? <Check size={18} /> : <Copy size={18} />}
                  {copied ? t('copied') : t('copy')}
                </button>
              </div>
              <div className="result-content">{improvedPrompt}</div>
            </div>
          )}

          {/* Prompt Type Selector - Moved below result */}
          <div className="prompt-type-selector">
            {promptTypes.map((type) => (
              <button
                key={type.type_key}
                className={`type-btn ${selectedType === type.type_key ? 'active' : ''}`}
                onClick={() => setSelectedType(type.type_key)}
                data-testid={`prompt-type-${type.type_key}`}
              >
                <span className="type-title">{type.title}</span>
                {type.subtitle && <span className="type-subtitle">{type.subtitle}</span>}
                <span className="type-desc">{type.description}</span>
              </button>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}

export default Dashboard;